import React, { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LayoutDashboard, Megaphone, Sparkles, BarChart3, Building2, CreditCard, Settings as SettingsIcon, LogOut, Plus, Send, Zap, X, Clock, Inbox, Gift, Menu } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiError } from '@/lib/api';
import { BoostaBolt, ZapBolt } from '@/components/BoostaBolt';
import { useAuth } from '@/contexts/AuthContext';

const LOW_ZAP_THRESHOLD = 100;

const NAV = [
  { to: '/dashboard', label: 'Command Centre', icon: LayoutDashboard, testid: 'side-dashboard' },
  { to: '/campaigns', label: 'Campaigns', icon: Megaphone, testid: 'side-campaigns' },
  { to: '/creatives', label: 'Creative Studio', icon: Sparkles, testid: 'side-creatives' },
  { to: '/composer', label: 'Publisher', icon: Send, testid: 'side-composer' },
  { to: '/analytics', label: 'Analytics', icon: BarChart3, testid: 'side-analytics' },
  { to: '/business-hub', label: 'Business Hub', icon: Building2, testid: 'side-business' },
  { to: '/payments', label: 'Payments', icon: CreditCard, testid: 'side-payments' },
  { to: '/settings', label: 'Settings', icon: SettingsIcon, testid: 'side-settings' },
];

export default function AppShell({ children }) {
  const { user, logout, refreshUser } = useAuth();
  const nav = useNavigate();
  const doLogout = async () => { await logout(); nav('/'); };

  const zap = user?.zap_credits ?? 0;
  const [bannerDismissed, setBannerDismissed] = useState(
    () => sessionStorage.getItem('lowZapDismissed') === '1');
  const showLowZap = user && zap < LOW_ZAP_THRESHOLD && !bannerDismissed;
  const dismissBanner = () => { sessionStorage.setItem('lowZapDismissed', '1'); setBannerDismissed(true); };

  // Trial countdown
  const trialDaysLeft = (() => {
    if (user?.plan_status !== 'trialing' || !user?.trial_ends_at) return null;
    const ms = new Date(user.trial_ends_at).getTime() - Date.now();
    if (isNaN(ms)) return null;
    return Math.max(0, Math.ceil(ms / 86400000));
  })();
  const [trialDismissed, setTrialDismissed] = useState(
    () => sessionStorage.getItem('trialCountdownDismissed') === '1');
  const showTrial = trialDaysLeft !== null && trialDaysLeft <= 5 && !trialDismissed;
  const dismissTrial = () => { sessionStorage.setItem('trialCountdownDismissed', '1'); setTrialDismissed(true); };
  const [extending, setExtending] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const extendTrial = async () => {
    setExtending(true);
    try {
      await api.post('/payments/extend-trial');
      toast.success('Your trial has been extended by 3 days — enjoy!');
      await refreshUser();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setExtending(false);
  };

  return (
    <div className="min-h-screen bg-boosta-ink relative">
      <div className="fixed inset-0 grid-bg opacity-40 pointer-events-none" />
      <div className="fixed inset-0 grain pointer-events-none" />

      <aside className="fixed inset-y-0 left-0 w-64 border-r border-white/5 bg-[#07080d]/80 backdrop-blur-xl z-40 hidden lg:flex flex-col">
        <Link to="/dashboard" className="flex items-center gap-2.5 px-6 py-6" data-testid="appshell-logo">
          <BoostaBolt size={36} animate/>
          <div>
            <div className="font-display font-black text-lg leading-none text-3d">Boosta</div>
            <div className="text-[10px] tracking-[0.2em] text-boosta-cyan uppercase mt-1">Command Centre</div>
          </div>
        </Link>
        <Link to="/campaigns/new" className="mx-4 mb-4 btn-primary rounded-xl px-4 py-3 text-sm flex items-center justify-center gap-2" data-testid="side-new-campaign">
          <Plus className="w-4 h-4"/> New Campaign
        </Link>
        <nav className="flex-1 px-3 space-y-1 nav-3d">
          {(user?.role === 'admin'
            ? [...NAV, { to: '/support-inbox', label: 'Support Inbox', icon: Inbox, testid: 'side-support' }]
            : NAV).map(({ to, label, icon: I, testid }, i) => (
            <motion.div key={to} initial={{ opacity: 0, x: -28 }} animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.06 * i, duration: 0.45, ease: [0.2, 0.8, 0.2, 1] }}>
              <NavLink to={to} data-testid={testid}
                className={({ isActive }) =>
                  `nav-item-3d flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm group border border-transparent ${
                    isActive
                      ? 'nav-item-active text-white'
                      : 'text-white/55 hover:text-white hover:bg-white/[0.03]'
                  }`}>
                <I className="w-4 h-4 transition-colors group-hover:text-boosta-cyan group-hover:drop-shadow-[0_0_8px_rgba(0,240,255,0.8)]" />
                <span className="nav-label font-display font-semibold tracking-wide">{label}</span>
              </NavLink>
            </motion.div>
          ))}
        </nav>
        <div className="p-4 border-t border-white/5">
          <div className="glass rounded-xl p-3 mb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] tracking-[0.2em] uppercase text-boosta-cyan font-bold">ZAP Balance</span>
              <ZapBolt size={14} />
            </div>
            <div className="font-display font-black text-2xl" data-testid="sidebar-zap-balance">{(user?.zap_credits ?? 0).toLocaleString()}</div>
            <Link to="/payments" className="text-xs text-boosta-cyan hover:underline mt-1 inline-block">Top up →</Link>
          </div>
          <div className="flex items-center gap-3 px-2">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-boosta-cyan to-boosta-emerald flex items-center justify-center text-boosta-ink font-bold text-sm">
              {user?.name?.[0]?.toUpperCase() ?? 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-[10px] text-white/40 uppercase tracking-wider">{user?.plan ?? 'free'}</div>
            </div>
            <button onClick={doLogout} className="text-white/50 hover:text-white" data-testid="logout-btn">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      <main className="lg:ml-64 relative z-10">
        <header className="lg:hidden glass border-b border-white/5 px-4 py-3 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="appshell-logo-mobile">
            <BoostaBolt size={28}/>
            <span className="font-display font-bold text-3d">Boosta</span>
          </Link>
          <div className="flex items-center gap-4">
            <button onClick={doLogout} className="text-white/60" data-testid="logout-btn-mobile"><LogOut className="w-4 h-4"/></button>
            <button onClick={() => setMobileNav(o => !o)} className="text-white/80" data-testid="mobile-nav-toggle">
              {mobileNav ? <X className="w-5 h-5"/> : <Menu className="w-5 h-5"/>}
            </button>
          </div>
        </header>
        {mobileNav && (
          <div className="lg:hidden glass border-b border-white/5 px-3 py-3 space-y-1" data-testid="mobile-nav-panel">
            {(user?.role === 'admin'
              ? [...NAV, { to: '/support-inbox', label: 'Support Inbox', icon: Inbox, testid: 'side-support' }]
              : NAV).map(({ to, label, icon: I, testid }) => (
              <NavLink key={to} to={to} data-testid={`m-${testid}`} onClick={() => setMobileNav(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm border border-transparent ${
                    isActive ? 'nav-item-active text-white' : 'text-white/60'
                  }`}>
                <I className="w-4 h-4" />
                <span className="font-display font-semibold tracking-wide">{label}</span>
              </NavLink>
            ))}
          </div>
        )}
        <div className="p-6 md:p-10 max-w-7xl mx-auto">
          {showTrial && (
            <div className="mb-6 glass rounded-2xl border border-boosta-cyan/30 px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4" data-testid="trial-countdown-banner">
              <div className="w-10 h-10 rounded-xl bg-boosta-cyan/10 flex items-center justify-center shrink-0">
                <Clock className="w-5 h-5 text-boosta-cyan" />
              </div>
              <div className="flex-1 min-w-[200px]">
                <div className="font-display font-bold text-sm text-white" data-testid="trial-countdown-text">
                  {trialDaysLeft === 0 ? 'Your free trial ends today' : `Your free trial ends in ${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'}`}
                </div>
                <div className="text-xs text-white/55 mt-0.5">Confirm your plan to keep your workspace active — no interruption.</div>
              </div>
              <Link to="/payments" className="btn-primary rounded-full px-5 py-2.5 text-sm inline-flex items-center gap-2" data-testid="trial-countdown-cta">
                <CreditCard className="w-4 h-4" /> Manage billing
              </Link>
              {!user?.trial_extended && (
                <button onClick={extendTrial} disabled={extending}
                  className="btn-ghost rounded-full px-5 py-2.5 text-sm inline-flex items-center gap-2 border border-boosta-yellow/40 text-boosta-yellow hover:text-white disabled:opacity-50"
                  data-testid="trial-extend-btn">
                  <Gift className="w-4 h-4" /> {extending ? 'Extending…' : 'Extend trial +3 days free'}
                </button>
              )}
              <button onClick={dismissTrial} className="text-white/40 hover:text-white p-1" title="Dismiss" data-testid="trial-countdown-dismiss">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          {showLowZap && (
            <div className="mb-6 glass rounded-2xl border border-boosta-yellow/30 px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4" data-testid="low-credit-banner">
              <div className="w-10 h-10 rounded-xl bg-boosta-yellow/10 flex items-center justify-center shrink-0">
                <Zap className="w-5 h-5 text-boosta-yellow" />
              </div>
              <div className="flex-1 min-w-[200px]">
                <div className="font-display font-bold text-sm text-white">
                  {zap === 0 ? "You're out of ZAP credits" : `Low ZAP balance — ${zap.toLocaleString()} left`}
                </div>
                <div className="text-xs text-white/55 mt-0.5">Top up to keep generating creatives and launching campaigns.</div>
              </div>
              <Link to="/payments" className="btn-primary rounded-full px-5 py-2.5 text-sm inline-flex items-center gap-2" data-testid="low-credit-topup-btn">
                <Zap className="w-4 h-4" /> Top up ZAP
              </Link>
              <button onClick={dismissBanner} className="text-white/40 hover:text-white p-1" title="Dismiss" data-testid="low-credit-dismiss-btn">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          {children}
        </div>
      </main>
    </div>
  );
}
